/* eslint-disable indent */
import React from 'react';
import { useParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { StatusTag } from '@/components/Tag/StatusTag';
import { FormField } from '@/components/Form/FormField';
import { PdfSectionHeading } from '../components/PdfSectionHeading';
import { PdfFormFieldValue } from '@/components/Pdf/components/PdfFormFieldValue';
import { PdfFormSection } from '@/components/Pdf/components/PdfFormSection';
import { PhotoWithMenu } from '@/components/Custom/Photo/PhotoWithMenu';
import { PdfPhotoListSection } from '../components/PdfPhotoListSection';
import { PdfHeader } from '../components/PdfHeader';
import { TVehiculeCheckSchema } from '@/schemas/vehicule-check/vehicule-check.schema';
import Loader from '@/components/Loading/Loader';
import { getSortedList } from '@/hooks/list-order';
import { extractListPhotos } from '@/modules/VehiculeCheckResult/utils/result-image.utils';
import { TFunction } from 'i18next';
import { TResultObjectSchema } from '@/schemas/vehicule-check-result/result-object.schema';
import { TVehiculeCheckResultSchema } from '@/schemas/vehicule-check-result/vehicule-check-result.schema';
import { ActionUserType } from '@/api/entities/vehicule-check';
import { ViewVehiculeCheckTab } from '@/type/ui/tab/vehiculeCheckTab';
import { ResultPhotoTab } from '@/type/ui/tab/vehiculeCheckResultTab';
import { PdfViewButton } from '@/components/Custom/Button/PdfButton/PdfViewButton';

interface ResultsPdfModelProps {
  t: TFunction<undefined>;
  isLoading?: boolean;
  vehiculeCheck?: TVehiculeCheckSchema;
  vehiculeCheckResult?: TVehiculeCheckResultSchema;
}

export const ResultsPdfModel = ({
  t,
  isLoading,
  vehiculeCheck,
  vehiculeCheckResult,
}: ResultsPdfModelProps) => {
  const { id } = useParams();

  const referenceNumber = vehiculeCheck?.referenceNumber;
  const licensePlateNumber =
    vehiculeCheck?.vehicule?.licensePlateNumber ||
    vehiculeCheckResult?.valueImmatriculation;
  const vin = vehiculeCheck?.vehicule?.vin || vehiculeCheckResult?.valueVin;
  const lastname =
    vehiculeCheck?.individualLastname?.toUpperCase() ||
    vehiculeCheck?.client?.lastname?.toUpperCase();
  const firstname =
    vehiculeCheck?.individualFirstname || vehiculeCheck?.client?.firstname;
  const email = vehiculeCheck?.individualEmail || vehiculeCheck?.client?.email;
  const phone = vehiculeCheck?.individualPhone || vehiculeCheck?.client?.phone;
  const address =
    (vehiculeCheck?.individualAddressLine1 ||
      vehiculeCheck?.client?.addressLine2) &&
    vehiculeCheck?.individualPostalCode &&
    vehiculeCheck?.individualCity &&
    vehiculeCheck?.individualCountry ? (
      <div>
        {vehiculeCheck?.individualAddressLine1 ||
          vehiculeCheck?.client?.addressLine2}
        <br />
        {vehiculeCheck?.individualPostalCode} {vehiculeCheck?.individualCity}
        <br />
        {vehiculeCheck?.individualCountry}
      </div>
    ) : undefined;

  // const getNbEntityBlocks = () => {
  //   let count = 0;
  //   if (vehiculeCheck?.client?.groups?.length > 0) {
  //     count++;
  //   }
  //   if (vehiculeCheck?.client?.companies?.length > 0) {
  //     count++;
  //   }
  //   if (vehiculeCheck?.client?.branches?.length > 0) {
  //     count++;
  //   }
  //   return count;
  // };

  // const nbEntityBlocks = getNbEntityBlocks();

  const groups =
    vehiculeCheck?.client?.groups?.length > 0 ? (
      <div>
        {vehiculeCheck?.client?.groups?.map((group) => (
          <div key={group?.id}>{group?.name}</div>
        ))}
      </div>
    ) : undefined;

  const companies =
    vehiculeCheck?.client?.companies?.length > 0 ? (
      <div>
        {vehiculeCheck?.client?.companies?.map((company) => (
          <div key={company?.id}>{company?.name}</div>
        ))}
      </div>
    ) : undefined;

  const branches =
    vehiculeCheck?.client?.branches?.length > 0 ? (
      <div>
        {vehiculeCheck?.client?.branches?.map((branch) => (
          <div key={branch?.id}>{branch?.name}</div>
        ))}
      </div>
    ) : undefined;

  const vehicules = extractListPhotos(
    vehiculeCheckResult?.results,
    'vehicules',
  ) as TResultObjectSchema[];

  const allEquipments = extractListPhotos(
    vehiculeCheckResult?.results,
    'equipments',
  ) as TResultObjectSchema[];

  const damages = extractListPhotos(
    vehiculeCheckResult?.results,
    'damages',
  ) as TResultObjectSchema[];

  return (
    <div>
      <PdfHeader
        title={t('inspectionReport')}
        date={vehiculeCheck?.createdAt}
      />

      {/* === INFORMATIONS === */}
      <div className="grid grid-cols-2 gap-x-5 gap-y-6">
        <PdfFormSection
          heading={
            <PdfSectionHeading
              text={t('common:informations')}
              icon={
                <i className="ki-filled ki-notepad-bookmark text-[30px] text-purple-A713F6" />
              }
              containerClassName="mb-4"
            />
          }
        >
          <>
            <div className="space-y-1.5">
              <div className="space-y-1.5">
                <FormField
                  text={t('inspectionId')}
                  textLeftIcon={
                    <i className="-mt-[1.5px] ki-outline ki-text-number text-[19px] text-gray-500" />
                  }
                  containerClassName="gap-0"
                  textOuterWrapperClassName="w-[180px] items-center"
                  textWrapperClassName="ml-2.25"
                  textClassName="text-black text-[15px] font-medium"
                >
                  <PdfFormFieldValue
                    text={vehiculeCheck?.shortId}
                    textClassName="-mt-1 border border-dashed border-black px-4 py-0.75 rounded"
                  />
                </FormField>
                <FormField
                  text={t('common:type')}
                  textLeftIcon={
                    <i className="-mt-[1.5px] ki-outline ki-abstract-45 text-[19px] text-gray-500" />
                  }
                  containerClassName="gap-0"
                  textOuterWrapperClassName="w-[180px] items-center"
                  textWrapperClassName="ml-2.25"
                  textClassName="text-black text-[15px] font-medium"
                >
                  <PdfFormFieldValue
                    text={
                      vehiculeCheck?.actionUserType ===
                      ActionUserType.INDIVIDUAL
                        ? t('common:individual')
                        : t('common:professional')
                    }
                    textClassName="mt-0.5"
                  />
                </FormField>
                {referenceNumber && (
                  <FormField
                    text={t('common:referenceNumber_short_3')}
                    textLeftIcon={
                      <i className="-mt-[1px] ki-outline ki-folder text-[19px] text-gray-500" />
                    }
                    containerClassName="gap-0"
                    textOuterWrapperClassName="w-[180px] items-center"
                    textWrapperClassName="mt-0.25 ml-2.25"
                    textClassName="text-black text-[15px] font-medium"
                  >
                    <PdfFormFieldValue
                      text={referenceNumber}
                      textClassName="mt-0.5"
                    />
                  </FormField>
                )}
              </div>
              <div className="space-y-1">
                <FormField
                  text={t('common:licensePlateNumber_short')}
                  textLeftIcon={
                    <StatusTag
                      text="123"
                      className="self-center px-1 pb-1.5 bg-secondary-light border-gray-DBDFE9 text-gray-78829D text-2xs font-normal dark-mode:border-gray-300 dark-mode:bg-gray-100 dark-mode:text-gray-700 dark-mode:font-normal"
                    />
                  }
                  containerClassName="gap-0"
                  textOuterWrapperClassName="w-[180px] items-center"
                  textWrapperClassName="mt-1"
                  textClassName="text-black text-[15px] font-medium"
                >
                  <PdfFormFieldValue
                    text={licensePlateNumber || '--'}
                    textClassName="mt-1.5"
                  />
                </FormField>
                <FormField
                  text={t('common:vin')}
                  textLeftIcon={
                    <StatusTag
                      text="VIN"
                      className="self-center px-1 pb-1.5 bg-secondary-light border-gray-DBDFE9 text-gray-78829D text-2xs font-normal dark-mode:border-gray-300 dark-mode:bg-gray-100 dark-mode:text-gray-700 dark-mode:font-normal"
                    />
                  }
                  containerClassName="gap-0"
                  textOuterWrapperClassName="w-[180px] items-center"
                  textWrapperClassName="mt-1"
                  textClassName="text-black text-[15px] font-medium"
                >
                  <PdfFormFieldValue
                    text={vin || '--'}
                    textClassName="mt-1.5"
                  />
                </FormField>
              </div>
            </div>
          </>
        </PdfFormSection>
        <PdfFormSection
          heading={
            <PdfSectionHeading
              text={t('common:performedBy_2_feminine')}
              icon={
                <i className="ki-filled ki-user-tick text-[30px] text-purple-A713F6" />
              }
              containerClassName="mb-4"
            />
          }
        >
          <div className="space-y-1.5">
            <FormField
              text={t('common:lastname')}
              containerClassName="gap-0"
              textOuterWrapperClassName="w-[180px] items-center"
              textClassName="text-black text-[15px] font-medium"
            >
              <PdfFormFieldValue text={lastname} textClassName="mt-0.5" />
            </FormField>
            <FormField
              text={t('common:firstname')}
              containerClassName="gap-0"
              textOuterWrapperClassName="w-[180px] items-center"
              textClassName="text-black text-[15px] font-medium"
            >
              <PdfFormFieldValue text={firstname} textClassName="mt-0.5" />
            </FormField>
            <FormField
              text={t('common:email')}
              containerClassName="gap-0"
              textOuterWrapperClassName="w-[180px] items-center"
              textClassName="text-black text-[15px] font-medium"
            >
              <PdfFormFieldValue text={email} textClassName="mt-0.5" />
            </FormField>
            {phone && (
              <FormField
                text={t('common:phoneNumber')}
                containerClassName="gap-0"
                textOuterWrapperClassName="w-[180px] items-center"
                textClassName="text-black text-[15px] font-medium"
              >
                <PdfFormFieldValue text={phone} textClassName="mt-0.5" />
              </FormField>
            )}
            {address && (
              <FormField
                text={t('common:address')}
                containerClassName="gap-0"
                textOuterWrapperClassName="w-[180px] items-center"
                textClassName="text-black text-[15px] font-medium"
              >
                <PdfFormFieldValue text={address} textClassName="mt-0.5" />
              </FormField>
            )}
          </div>
        </PdfFormSection>
        {/* === VIEW BUTTON === */}
        {/* {nbEntityBlocks === 1 && (
          <PdfViewButton
            href={`/vehicule-check/view/${id}?tab=${ViewVehiculeCheckTab.DETAILED_RESULTS}&childTab=${ResultPhotoTab.VEHICULES}`}
            buttonText={t('viewOnlineReport')}
            containerClassName="-mt-1.75"
          />
        )} */}
        {/* === END OF VIEW BUTTON === */}
      </div>
      {/* === END OF INFORMATIONS === */}

      <div className="mt-5 grid grid-cols-2 gap-x-5 gap-y-3">
        {groups && (
          <PdfFormSection heading="">
            <FormField
              text={
                vehiculeCheck?.client?.groups?.length > 1
                  ? t('common:groups')
                  : t('common:group')
              }
              textLeftIcon={
                <i className="-mt-[3px] ki-filled ki-abstract-20 text-[23px] text-purple-A713F6" />
              }
              containerClassName="gap-0"
              textOuterWrapperClassName="w-[180px] items-center"
              textWrapperClassName="ml-2.25"
              textClassName="text-black text-[17px] font-semibold"
            >
              <PdfFormFieldValue
                text={groups}
                textClassName="mt-0.5 uppercase"
              />
            </FormField>
          </PdfFormSection>
        )}
        {companies && (
          <PdfFormSection heading="">
            <FormField
              text={
                vehiculeCheck?.client?.companies?.length > 1
                  ? t('common:companies')
                  : t('common:company')
              }
              textLeftIcon={
                <i className="-mt-[3px] ki-filled ki-abstract-20 text-[23px] text-purple-A713F6" />
              }
              containerClassName="gap-0"
              textOuterWrapperClassName="w-[180px] items-center"
              textWrapperClassName="ml-2.25"
              textClassName="text-black text-[17px] font-semibold"
            >
              <PdfFormFieldValue
                text={companies}
                textClassName="mt-0.5 uppercase"
              />
            </FormField>
          </PdfFormSection>
        )}
        {branches && (
          <PdfFormSection heading="">
            <FormField
              text={
                vehiculeCheck?.client?.branches?.length > 1
                  ? t('common:branches')
                  : t('common:branch')
              }
              textLeftIcon={
                <i className="-mt-[3px] ki-filled ki-abstract-20 text-[23px] text-purple-A713F6" />
              }
              containerClassName="gap-0"
              textOuterWrapperClassName="w-[180px] items-center"
              textWrapperClassName="ml-2.25"
              textClassName="text-black text-[17px] font-semibold"
            >
              <PdfFormFieldValue
                text={branches}
                textClassName="mt-0.5 uppercase"
              />
            </FormField>
          </PdfFormSection>
        )}
      </div>

      {/* === VIEW BUTTON === */}
      {/* {nbEntityBlocks !== 1 && ( */}
      <div className="mt-5 flex justify-center items-center gap-3">
        <PdfViewButton
          href={`/vehicule-check/view/${id}?tab=${ViewVehiculeCheckTab.VIDEOS}`}
          buttonText={t('viewVideo')}
          buttonClassName="bg-transparent border-primary"
          iconClassName="ki-youtube text-primary"
          textClassName="text-primary text-[13px]"
        />
        <div className="text-gray-400 text-xl font-light">|</div>
        <PdfViewButton
          href={`/vehicule-check/view/${id}?tab=${ViewVehiculeCheckTab.DETAILED_RESULTS}&childTab=${ResultPhotoTab.VEHICULES}`}
          buttonText={t('viewOnlineReport')}
        />
      </div>
      {/* === END OF VIEW BUTTON === */}

      {/* === VEHICULE LIST === */}
      {vehicules?.length > 0 && (
        <React.Fragment>
          <PdfSectionHeading
            text={t('common:vehicule')}
            icon={
              <i className="ki-duotone ki-category text-[30px] font-extralight text-primary" />
            }
            containerClassName="mt-10 mb-2.5"
          />
          <PdfPhotoListSection
            heading=""
            isNotFound={!vehicules?.length}
            notFoundText={t('thisInspectionDoesHaveVehiculePhotos')}
          >
            {getSortedList(vehicules, 'updatedAt')?.map((item) => {
              const checkPoint = item?.['checkPoint'];

              return (
                <a
                  key={checkPoint?.id}
                  href={`/vehicule-check/view/${id}?tab=${ViewVehiculeCheckTab.DETAILED_RESULTS}&childTab=${ResultPhotoTab.EQUIPMENTS}&checkPointId=${checkPoint?.id}&checkPointImageUrl=${checkPoint?.imageCheckPoint}`}
                  target="_blank"
                >
                  <PhotoWithMenu
                    url={checkPoint?.imageCheckPoint}
                    hideActionButtons
                    disableLazyLoading
                    // showInfoWithinImage
                    // name={
                    //   <div className="text-2sm text-center">{checkPoint?.name}</div>
                    // }
                    photoNameClassName="text-2sm text-black font-normal leading-tight"
                    name={checkPoint?.name}
                  />
                </a>
              );
            })}
          </PdfPhotoListSection>
        </React.Fragment>
      )}
      {/* === END OF VEHICULE LIST === */}

      {/* === EQUIPMENT LIST === */}
      {allEquipments?.length > 0 && (
        <React.Fragment>
          <PdfSectionHeading
            text={
              <div>
                {t('common:equipments')}
                {/* <span className="mx-2 text-2sm font-semibold">-</span>
                <span className="text-2sm font-semibold leading-tight">
                  {`${t('common:totalEquipments')} : ${allEquipments?.length}`}
                </span>
                <span className="mx-2 text-[10px] font-normal">|</span>
                <span className="text-2sm font-semibold leading-tight">
                  {`${t('common:totalDamages')} : ${damages?.length}`}
                </span> */}
                <span className="mx-2 text-sm font-medium">-</span>
                <span className="text-sm font-medium leading-tight">
                  {`${t('common:total')} : ${allEquipments?.length}`}
                </span>
              </div>
            }
            icon={
              <i className="ki-duotone ki-toggle-on text-[30px] font-extralight text-primary" />
            }
            containerClassName="mt-10 mb-2"
          />
          {vehiculeCheckResult?.results
            ?.filter(
              (item) =>
                item?.checkPoint?.isVideo &&
                item?.checkPoint?.equipments?.length &&
                item?.checkPoint?.equipments?.some(
                  (equipment) =>
                    Array.isArray(equipment?.images) &&
                    equipment?.images?.length > 0 &&
                    equipment?.images?.some((image) => !image?.isDeleted),
                ),
            )
            ?.map((item, index) => {
              const checkPoint = item?.checkPoint;
              const equipments = checkPoint?.equipments;

              return (
                // <div key={checkPoint?.id} className="grid grid-cols-1 break-inside-avoid">
                <React.Fragment key={checkPoint?.id}>
                  <div
                    className={twMerge(
                      'grid grid-cols-1 break-inside-avoid mb-2',
                      index > 0 && 'mt-5.5',
                    )}
                  >
                    <div className="flex justify-between items-center gap-2 px-3 pt-1.5 pb-1 bg-primary rounded-tl-md rounded-tr-md">
                      <div className="flex items-center gap-2">
                        <i className="ki-outline ki-up-square text-white text-2xl rotate-180" />
                        <div className="-mt-0.5 text-2sm text-white font-bold uppercase">
                          {checkPoint?.name}
                        </div>
                        <span className="text-[10px] font-bold text-white/90">
                          -
                        </span>
                        <div className="text-white font-light text-2sm">
                          {`${t('common:nbEquipments')} : ${equipments?.length}`}
                          {/* <span className="mx-2 text-[10px] font-bold text-white/90">
                            |
                          </span>
                          {`${t('common:nbDamages')} : ${checkPoint?.damages?.length}`} */}
                        </div>
                      </div>
                      <PdfViewButton
                        href={`/vehicule-check/view/${id}?tab=${ViewVehiculeCheckTab.DETAILED_RESULTS}&childTab=${ResultPhotoTab.EQUIPMENTS}&checkPointId=${checkPoint?.id}`}
                        buttonText={t('viewVideo')}
                        containerClassName="-mt-[2px]"
                        buttonClassName="!h-7 items-center bg-transparent border-white rounded"
                        iconClassName="ki-youtube text-white text-[12px]"
                        textClassName="text-white text-xs"
                      />
                    </div>
                  </div>
                  <PdfPhotoListSection heading="">
                    {getSortedList(equipments, 'updatedAt')?.map((item) => {
                      return (
                        <a
                          key={item?.id}
                          href={`/vehicule-check/view/${id}?tab=${ViewVehiculeCheckTab.DETAILED_RESULTS}&childTab=${ResultPhotoTab.EQUIPMENTS}&checkPointId=${checkPoint?.id}&resultObjectId=${item?.id}`}
                          target="_blank"
                        >
                          <PhotoWithMenu
                            url={item?.images?.[0]?.url}
                            detectionCoordinates={
                              item?.images?.[0]?.['coordinatesModerated']
                            }
                            hideActionButtons
                            disableLazyLoading
                            // showInfoWithinImage
                            // name={
                            //   <div className="text-2sm text-center">
                            //     {item?.equipment?.name}
                            //   </div>
                            // }
                            photoNameClassName="text-2sm text-black font-normal leading-tight"
                            name={item?.equipment?.name}
                          />
                        </a>
                      );
                    })}
                  </PdfPhotoListSection>
                </React.Fragment>
              );
            })}
        </React.Fragment>
      )}
      {/* === END OF EQUIPMENT LIST === */}

      {/* === DAMAGE LIST === */}
      {damages?.length > 0 && (
        <React.Fragment>
          <PdfSectionHeading
            text={
              <div>
                {t('common:damages')}
                <span className="mx-2 text-sm font-medium">-</span>
                <span className="text-sm font-medium leading-tight">
                  {`${t('common:total')} : ${damages?.length}`}
                </span>
              </div>
            }
            icon={
              <i className="ki-duotone ki-abstract-25 text-[30px] font-extralight text-primary" />
            }
            containerClassName="mt-10 mb-2.5"
          />
          <PdfPhotoListSection
            heading=""
            isNotFound={!damages?.length}
            notFoundText={t('thisInspectionDoesHaveDamagePhotos')}
          >
            {getSortedList(damages, 'updatedAt')?.map((item) => {
              return (
                <a
                  key={item?.id}
                  href={`/vehicule-check/view/${id}?tab=${ViewVehiculeCheckTab.DETAILED_RESULTS}&childTab=${ResultPhotoTab.EQUIPMENTS}&toDamageList=true&resultObjectId=${item?.id}`}
                  target="_blank"
                >
                  <PhotoWithMenu
                    url={item?.images?.[0]?.url}
                    detectionCoordinates={
                      item?.images?.[0]?.['coordinatesModerated']
                    }
                    originalBoundingBox={item?.['original_bbox']}
                    hideActionButtons
                    disableLazyLoading
                    // showInfoWithinImage
                    photoNameClassName="text-2sm text-black font-normal text-start"
                    name={
                      <div className="px-1.5 leading-tight space-y-0.5">
                        <div className="leading-tight">
                          <span className="text-2sm font-semibold leading-tight">
                            {t('common:equipment')} :
                          </span>
                          &nbsp;
                          <span className="text-2sm leading-tight">
                            {item?.equipment?.name}
                          </span>
                        </div>
                        <div className="relative -top-[0.25px] leading-tight">
                          <span className="text-2sm font-semibold leading-tight">
                            {t('common:damage')} :
                          </span>
                          &nbsp;
                          <span className="text-2sm leading-tight">
                            {item?.damage?.name?.split?.('_')?.[0]}
                          </span>
                        </div>
                        <div className="leading-tight">
                          <span className="text-2sm font-semibold leading-tight">
                            {t('common:severity')} :
                          </span>
                          &nbsp;
                          <span className="text-2sm leading-tight">
                            {item?.damage?.severity}
                          </span>
                        </div>
                      </div>
                    }
                  />
                </a>
              );
            })}
          </PdfPhotoListSection>
        </React.Fragment>
      )}
      {/* === END OF DAMAGE LIST === */}
      {isLoading && <Loader />}
    </div>
  );
};
